import './messenger.css';

// Core / React
import {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useReducer,
  useMemo,
  useLayoutEffect,
} from 'react';

// Libs
import { useReactMediaRecorder } from 'react-media-recorder';
import { useIsTyping } from 'use-is-typing';
import { useQuery } from 'react-query';
import LoadingBar from 'react-top-loading-bar';
import { CircularProgress } from '@material-ui/core';
import { useParams } from 'react-router-dom';

// Contexts / Hooks
import { AuthContext } from '../../context/AuthContext';
import { SocketContext } from '../../context/socket';
import { ViewPortContext } from '../../context/ViewPortContext';
import { useTheme } from '../../context/ThemeContext';
import { useSuspension } from '../../context/SuspensionContext';
import { useGetConversations } from '../../hooks/useGetConversations';
import useMobileView from '../../hooks/useMobileView';
import useClickOutside from '../../clickOutside';
import { useConfettiText } from '../../hooks/useConfettiText';
import { useCall } from '../../context/CallContext';

// Components
import Conversation from '../../components/conversations/Conversation';
import Message from '../../components/message/Message';
import ChatBoxMiddle from '../../components/chatBoxMiddle/ChatBoxMiddle';
import ChatBoxBottom from '../../components/chatBoxBottom/ChatBoxBottom';
import TypingIndicator from '../../components/typingIndicator/TypingIndicator';

// Utils
import axios from 'axios';
import axiosConn from '../../axiosConn';
import { handleEmoji as insertEmoji } from '../../utils/handleEmojis';
import { handleSuspension } from '../../utils/suspensionUtil';
import { usePresence } from '../../context/PresenceContext';
import { INCOMING_MESSAGE_SQUEAK_SOUND_URL2 } from '../../utils/constants';
import { MAX_VIDEO_SIZE, MAX_IMAGE_SIZE } from '../../config/constants';
import handleFileInput from '../../utils/handleFileInput';
import {
  createMediaUploader,
  DEFAULT_VIDEO_MIME_TYPES,
} from '../../utils/mediaUpload';
import {
  getMessengerUploadErrorMessage,
  MESSENGER_UPLOAD_ACCEPT,
  prepareMessengerUploadFile,
} from '../../utils/messengerUploadPrep';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MAX_VIDEO_SIZE_MB = MAX_VIDEO_SIZE / (1024 * 1024);
const MESSAGE_PARTICLE_COUNT = 100;

const MESSAGE_LIMIT = 12;
const TIME_WINDOW = 60000;
const SUSPENSION_TIME = 3600000;
const AUTO_SCROLL_BOTTOM_THRESHOLD = 120;

const SHOW_RIGHT_PRESENCE = false;
const PRESENCE_DRAFT_KEY = 'jg_presence_draft_message';
const PRESENCE_DRAFT_TARGET_KEY = 'jg_presence_draft_target';
const PRESENCE_DRAFT_CONVO_KEY = 'jg_presence_draft_convo';

const TYPING_SOUND_URL =
  'https://res.cloudinary.com/joegalaxy/video/upload/q_auto/v1668749345/Keyboard-Typing-00-c-FesliyanStudios.com_ercgzd.mp3';

const MESSENGER_VIDEO_AUDIO_MIME_TYPES = new Set([
  ...DEFAULT_VIDEO_MIME_TYPES,
  'audio/mpeg',
  'audio/mpeg-3',
  'audio/mp3',
  'audio/mp4',
  'audio/aac',
  'audio/wav',
  'audio/webm',
  'audio/ogg',
]);

const isMessengerVideoOrAudioName = (name = '') =>
  /\.(mp4|m4v|webm|ogg|ogv|mov|flv|3gp|3g2|avi|mkv|mp3|m4a|aac|wav|oga)$/i.test(
    String(name || '').split('?')[0],
  );

function deriveInitialConvoLimit() {
  const h = typeof window !== 'undefined' ? window.innerHeight : 0;
  if (!h) return 5;
  if (h < 580) return 4;
  if (h < 720) return 5;
  if (h < 860) return 6;
  return 7;
}
const BASE_INITIAL_CONVO_LIMIT = 5;
const MOBILE_INITIAL_CONVO_LIMIT = 3;
const CONVO_INCREMENT = 15;

const BOTTOM_EPS = 60;
const TOP_EPS = 40;

function getMessageKey(message) {
  return message?._id || message?.messageId || null;
}

function appendUniqueMessage(state, message) {
  const key = getMessageKey(message);
  if (key && state.some((entry) => getMessageKey(entry) === key)) {
    return state;
  }
  return [...state, message];
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
function messagesReducer(state, action) {
  switch (action.type) {
    case 'SET_ALL':
      return action.payload;
    case 'APPEND':
      return appendUniqueMessage(state, action.payload);
    case 'BATCH_APPEND':
      return action.payload.reduce(appendUniqueMessage, state);
    case 'DELETE':
      return state.filter((m) => getMessageKey(m) !== action.id);
    case 'MARK_READ_UP_TO':
      return state.map((m) =>
        m._id <= action.messageId && !m.isRead ? { ...m, isRead: true } : m,
      );
    case 'RESET':
      return [];
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Messenger() {
  // Context
  const { player } = useContext(AuthContext);
  const { socket } = useContext(SocketContext);
  const presenceCtx = usePresence() || {};
  const presenceMap = useMemo(() => {
    const pm = presenceCtx.presenceMap || {};
    if (pm instanceof Map) return pm;
    return new Map(Object.entries(pm));
  }, [presenceCtx]);
  const [fallbackPresenceMap, setFallbackPresenceMap] = useState(
    () => new Map(),
  );
  const { width } = useContext(ViewPortContext);
  const { darkTheme } = useTheme();
  const { isSuspended, setIsSuspended } = useSuspension();

  // Route
  const { convoId } = useParams();

  // Call context (only for starting calls)
  const { startCall: startCallFromContext } = useCall();

  // State
  const [conversations, setConversations] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [receiverProfilePhoto, setReceiverProfilePhoto] = useState(null);
  const [messages, dispatchMessages] = useReducer(messagesReducer, []);
  const [picker, setPicker] = useState(false);
  const [text, setText] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      return sessionStorage.getItem(PRESENCE_DRAFT_KEY) || '';
    } catch {
      return '';
    }
  });
  const [cursorPosition, setCursorPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const DEFAULT_MESSAGE_COUNT = 6;
  const MESSAGE_INCREMENT = 6;
  const [messageCount, setMessageCount] = useState(DEFAULT_MESSAGE_COUNT);
  const mobileView = useMobileView();
  const derivedInitialLimit = useMemo(
    () => Math.max(BASE_INITIAL_CONVO_LIMIT, deriveInitialConvoLimit()),
    [],
  );
  const INITIAL_CONVO_LIMIT = derivedInitialLimit;
  const [conversationCount, setConversationCount] = useState(() =>
    mobileView ? MOBILE_INITIAL_CONVO_LIMIT : derivedInitialLimit,
  );
  useEffect(() => {
    setConversationCount((count) =>
      mobileView
        ? Math.min(count, MOBILE_INITIAL_CONVO_LIMIT)
        : Math.max(count, derivedInitialLimit),
    );
  }, [mobileView, derivedInitialLimit]);
  const [voiceMemo, setVoiceMemo] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
  const [isTypingDisplay, setIsTypingDisplay] = useState(false);
  const isSendingRef = useRef(false);
  const [senderPlayerId, setSenderPlayerId] = useState(null);
  const [localMessageCount, setLocalMessageCount] = useState(0);
  const [startTime, setStartTime] = useState(Date.now());
  const [messageTimestamps, setMessageTimestamps] = useState([]);

  // Media Recorder (voice memos)
  const {
    status: recStatus,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
  } = useReactMediaRecorder({ audio: true });

  const uploaderRef = useRef(null);
  if (!uploaderRef.current) {
    uploaderRef.current = createMediaUploader({
      axiosInstance: axios,
      maxImageBytes: MAX_IMAGE_SIZE,
      maxVideoBytes: MAX_VIDEO_SIZE,
      videoMimeTypes: MESSENGER_VIDEO_AUDIO_MIME_TYPES,
      isVideoName: isMessengerVideoOrAudioName,
      forceAutoResourceType: true,
      enableAdaptiveLadder: false,
      enableThumbnails: false,
      enableImageDimensionCheck: false,
      enableVideoMetaCheck: false,
      signed: false,
    });
  }
  const uploader = uploaderRef.current;

  // Typing hook
  const [isTyping, register] = useIsTyping({ timeout: 350 });

  // Refs
  const messageListRef = useRef(null);
  const bottomAnchorRef = useRef(null);
  const textRef = useRef(null);
  const menuRef = useRef(null);
  const fileInput = useRef(null);
  const messageSoundRef = useRef(null);
  const typingSoundRef = useRef(null);
  const receiverPhotoCache = useRef(new Map());
  const mountedRef = useRef(false);
  const topbarRef = useRef(null);
  const userInteractedRef = useRef(false);
  const participantKeysRef = useRef(new Set());
  const requestedPresenceSnapshotRef = useRef(false);
  const presenceSnapshotThrottleRef = useRef(0);

  // Scroll flags
  const [atBottom, setAtBottom] = useState(true);
  const [nearTop, setNearTop] = useState(true);

  // Confetti
  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    textRef,
    mobileView,
    '+1 JGK So Fun!',
    MESSAGE_PARTICLE_COUNT,
    'messenger',
    voiceMemo,
  );

  // Topbar height -> CSS var
  useLayoutEffect(() => {
    const el = topbarRef.current || document.querySelector('.topbarNav');
    if (!el) return;
    const setVar = () =>
      document.documentElement.style.setProperty(
        '--topbar-h',
        el.getBoundingClientRect().height + 'px',
      );
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener('orientationchange', setVar);
    window.addEventListener('resize', setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', setVar);
      window.removeEventListener('resize', setVar);
    };
  }, []);

  const { messageDispatchRef } = useCall();

  useEffect(() => {
    if (messageDispatchRef) {
      messageDispatchRef.current = dispatchMessages;
    }
  }, [messageDispatchRef, dispatchMessages]);

  // Mount flag
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Clear handoff draft once Messenger is mounted
  useEffect(() => {
    try {
      sessionStorage.removeItem(PRESENCE_DRAFT_KEY);
      sessionStorage.removeItem(PRESENCE_DRAFT_TARGET_KEY);
      sessionStorage.removeItem(PRESENCE_DRAFT_CONVO_KEY);
    } catch {
      /* ignore storage failures */
    }
  }, []);

  // Reset local confetti window
  useEffect(() => {
    const interval = setInterval(() => {
      setLocalMessageCount(0);
      setStartTime(Date.now());
    }, TIME_WINDOW);
    return () => clearInterval(interval);
  }, []);

  // Click outside emoji picker
  useClickOutside(menuRef, () => setPicker(false));

  // Maintain caret after emoji insert
  useEffect(() => {
    if (cursorPosition != null && textRef.current) {
      textRef.current.selectionEnd = cursorPosition;
      textRef.current.selectionStart = cursorPosition;
    }
  }, [cursorPosition]);

  // Scroll listener
  useEffect(() => {
    const el = messageListRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const distFromBottom = scrollHeight - (scrollTop + clientHeight);
      setAtBottom(distFromBottom < BOTTOM_EPS);
      setNearTop(scrollTop < TOP_EPS);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll at bottom
  useEffect(() => {
    if (atBottom) {
      requestAnimationFrame(() =>
        bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth' }),
      );
    }
  }, [messages, atBottom]);

  // Focus after history load
  useEffect(() => {
    if (currentChat && isHistoryLoaded) {
      requestAnimationFrame(() => {
        bottomAnchorRef.current?.scrollIntoView({ behavior: 'auto' });
        textRef.current?.focus();
      });
    }
  }, [currentChat, isHistoryLoaded]);

  // Load conversation from param
  useEffect(() => {
    if (!convoId || convoId === `''`) {
      setCurrentChat(null);
      dispatchMessages({ type: 'RESET' });
      return;
    }
    let abort = false;
    (async () => {
      try {
        const res = await axiosConn.get(
          `conversations/${convoId}/conversation`,
          {
            headers: { Authorization: `Bearer ${player.token}` },
          },
        );
        if (abort) return;
        if (res.data.data.data.members.includes(player.data.player._id)) {
          setCurrentChat(res.data.data.data);
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      abort = true;
    };
  }, [convoId, player.token, player.data.player._id]);

  // Conversations
  const {
    data: convData,
    isSuccess: convSuccess,
    refetch: refetchConversations,
  } = useGetConversations(
    () => {},
    () => {},
    player,
  );

  useEffect(() => {
    if (convSuccess) setConversations(convData);
  }, [convSuccess, convData]);

  // When conversations load, activate the one from the route (if present)
  useEffect(() => {
    if (!convoId || currentChat) return;
    const found = conversations.find(
      (c) => String(c?._id || c?.id) === String(convoId),
    );
    if (found) {
      setCurrentChat(found);
      dispatchMessages({ type: 'RESET' });
    }
  }, [convoId, conversations, currentChat]);

  useEffect(() => {
    const nextKeys = new Set(participantKeysRef.current);
    conversations.forEach((convo) => {
      (convo?.members || []).forEach((memberId) => {
        if (!memberId || memberId === player.data.player._id) return;
        nextKeys.add(String(memberId));
      });
    });
    participantKeysRef.current = nextKeys;
  }, [conversations, player.data.player._id]);

  const normalizePresenceStatus = useCallback((status, onlineFlag) => {
    if (typeof onlineFlag === 'boolean' && onlineFlag === false) return 'offline';
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'online' || normalized === 'active' || normalized === 'available')
      return 'online';
    if (normalized === 'idle' || normalized === 'away' || normalized === 'background')
      return normalized === 'background' ? 'idle' : normalized;
    if (normalized === 'offline') return 'offline';
    if (onlineFlag === true) return 'online';
    return 'offline';
  }, []);

  const upsertFallbackPresence = useCallback(
    (payload) => {
      if (!payload) return;
      const keys = [
        payload.playerId,
        payload.handle,
        payload._id,
        payload.mongoId,
        payload.accountId,
        payload.id,
        payload?.source?._id,
        payload?.source?.playerId,
      ]
        .filter(Boolean)
        .map((val) => String(val));
      const relevantKeys = keys.filter((key) =>
        participantKeysRef.current.has(key),
      );
      if (!relevantKeys.length) return;
      const status = normalizePresenceStatus(
        payload.status || payload.presenceStatus,
        payload.online,
      );
      const lastSeen =
        payload.lastSeenAt ||
        payload.lastSeen ||
        payload.updatedAt ||
        payload.timestamp ||
        Date.now();
      const entry = {
        status,
        presenceStatus: status,
        online: status !== 'offline',
        lastSeen,
        lastSeenAt: lastSeen,
        updatedAt: lastSeen,
      };
      setFallbackPresenceMap((prev) => {
        const next = new Map(prev);
        relevantKeys.forEach((key) => {
          next.set(key, entry);
        });
        return next;
      });
    },
    [normalizePresenceStatus],
  );

  useEffect(() => {
    if (!socket) return undefined;
    const handlePlayer = (payload) => upsertFallbackPresence(payload);
    const handleUpdate = (payload) => upsertFallbackPresence(payload);
    const handleDiff = (payload) => {
      const updates = []
        .concat(payload?.add || [])
        .concat(payload?.update || [])
        .concat(payload?.updates || [])
        .concat(payload?.joins || []);
      updates.forEach((entry) => upsertFallbackPresence(entry));
      const leaves = []
        .concat(payload?.remove || [])
        .concat(payload?.leaves || []);
      leaves.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        upsertFallbackPresence({ ...entry, status: 'offline' });
      });
    };
    const handlePlayerSnapshot = (payload) => {
      const players = payload?.players || payload?.data || payload || [];
      const list = Array.isArray(players)
        ? players
        : Object.entries(players).map(([, val]) => val);
      if (!list.length) return;
      setFallbackPresenceMap((prev) => {
        let next = new Map(prev);
        let changed = false;
        list.forEach((entry) => {
          if (!entry) return;
          const keys = [
            entry.playerId,
            entry.handle,
            entry._id,
            entry.mongoId,
            entry.accountId,
            entry.id,
            entry?.source?._id,
            entry?.source?.playerId,
          ]
            .filter(Boolean)
            .map((val) => String(val));
          const relevantKeys = keys.filter((key) =>
            participantKeysRef.current.has(key),
          );
          if (!relevantKeys.length) return;
          const status = normalizePresenceStatus(
            entry.status || entry.presenceStatus,
            entry.online,
          );
          const lastSeen =
            entry.lastSeenAt ||
            entry.lastSeen ||
            entry.updatedAt ||
            entry.timestamp ||
            Date.now();
          const normalizedEntry = {
            status,
            presenceStatus: status,
            online: status !== 'offline',
            lastSeen,
            lastSeenAt: lastSeen,
            updatedAt: lastSeen,
          };
          relevantKeys.forEach((key) => {
            next.set(key, normalizedEntry);
            changed = true;
          });
        });
        return changed ? next : prev;
      });
    };
    const handleSnapshot = (payload) => {
      if (!payload?.players && !payload?.data) return;
      handlePlayerSnapshot(payload);
    };
    const handlePlayerStatus = (payload) => upsertFallbackPresence(payload);

    socket.on('presence:player', handlePlayer);
    socket.on('player:status', handlePlayerStatus);
    socket.on('presence:snapshot', handleSnapshot);
    socket.on('player-snapshot', handlePlayerSnapshot);
    socket.on('presence:update', handleUpdate);
    socket.on('presence:diff', handleDiff);
    socket.on('presence:remove', handleDiff);
    socket.on('presence:remove:detail', handleDiff);

    if (!requestedPresenceSnapshotRef.current) {
      socket.emit('presence:snapshot:request');
      requestedPresenceSnapshotRef.current = true;
    }

    return () => {
      socket.off('presence:player', handlePlayer);
      socket.off('player:status', handlePlayerStatus);
      socket.off('presence:snapshot', handleSnapshot);
      socket.off('player-snapshot', handlePlayerSnapshot);
      socket.off('presence:update', handleUpdate);
      socket.off('presence:diff', handleDiff);
      socket.off('presence:remove', handleDiff);
      socket.off('presence:remove:detail', handleDiff);
    };
  }, [socket, upsertFallbackPresence, normalizePresenceStatus]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!currentChat?._id) return [];
    try {
      const res = await axiosConn.get(`messages/${currentChat._id}`, {
        headers: { Authorization: `Bearer ${player.token}` },
      });
      return res.data.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );
    } catch {
      return [];
    }
  }, [currentChat?._id, player.token]);

  const {
    data: fetchedMessages,
    status: messagesStatus,
    isLoading: isLoadingMessages,
  } = useQuery(['getMessages', currentChat?._id], fetchMessages, {
    enabled: !!currentChat?._id,
    keepPreviousData: false,
    placeholderData: [],
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const lastMessagesKeyRef = useRef(null);

  useEffect(() => {
    // Clear previous thread immediately when switching conversations
    dispatchMessages({ type: 'RESET' });
    setIsHistoryLoaded(false);
    lastMessagesKeyRef.current = null;
  }, [currentChat?._id]);

  useEffect(() => {
    if (messagesStatus === 'success' && fetchedMessages) {
      const key = `${currentChat?._id || 'none'}:${fetchedMessages.length}`;
      if (lastMessagesKeyRef.current === key) return;
      lastMessagesKeyRef.current = key;

      dispatchMessages({ type: 'SET_ALL', payload: fetchedMessages });
      setIsHistoryLoaded(true);
      refetchConversations();
      const last = fetchedMessages[fetchedMessages.length - 1];
      if (last && last.sender !== player.data.player._id) {
        socket?.emit('messageRead', {
          messageId: last._id,
          isRead: true,
          senderId: last.sender,
          receiverId: player.data.player._id,
        });
      }
      requestAnimationFrame(() => {
        bottomAnchorRef.current?.scrollIntoView({ behavior: 'auto' });
      });
    }
  }, [
    messagesStatus,
    fetchedMessages,
    player.data.player._id,
    socket,
    refetchConversations,
  ]);

  // Receiver profile photo
  useEffect(() => {
    const receiverId = currentChat?.members?.find(
      (m) => m !== player.data.player._id,
    );
    if (!receiverId) {
      setReceiverProfilePhoto(null);
      return;
    }
    if (receiverPhotoCache.current.has(receiverId)) {
      setReceiverProfilePhoto(receiverPhotoCache.current.get(receiverId));
      return;
    }
    let abort = false;
    (async () => {
      try {
        const res = await axiosConn.get(`players/${receiverId}`, {
          headers: { Authorization: `Bearer ${player.token}` },
        });
        if (abort) return;
        const photo = res.data?.data?.data?.profilePhoto || null;
        receiverPhotoCache.current.set(receiverId, photo);
        setReceiverProfilePhoto(photo);
      } catch {
        if (!abort) setReceiverProfilePhoto(null);
      }
    })();
    return () => {
      abort = true;
    };
  }, [currentChat?.members, player.data.player._id, player.token]);

  // Socket events (legacy messaging)
  const registerSocketEvents = useCallback(() => {
    if (!socket) return;
    socket.emit('addPlayer', player.data.player._id);

    const handleIncomingMessage = (data) => {
      if (data.localOnly) return;

      if (!mountedRef.current) return;
      const isOwnMessage =
        String(data.senderId) === String(player.data.player._id);
      const incoming = {
        _id: data.messageId,
        sender: data.senderId,
        text: data.text,
        img: data.img,
        createdAt: Date.now(),
        messageId: data.messageId,
      };
      if (currentChat?.members?.includes(incoming.sender) && isHistoryLoaded) {
        dispatchMessages({ type: 'APPEND', payload: incoming });
        if (!isOwnMessage) {
          socket.emit('messageRead', {
            messageId: incoming.messageId,
            isRead: true,
            senderId: incoming.sender,
            receiverId: player.data.player._id,
          });
        }
        refetchConversations();
      }
      if (isOwnMessage) return;
      if (!messageSoundRef.current) {
        messageSoundRef.current = new Audio(INCOMING_MESSAGE_SQUEAK_SOUND_URL2);
      }
      messageSoundRef.current.play().catch(() => {});
    };

    const handleMessageRead = (data) => {
      if (data.senderId === player.data.player._id) {
        dispatchMessages({
          type: 'MARK_READ_UP_TO',
          messageId: data.messageId,
        });
      }
    };

    const handleClearChat = (conversationId) => {
      if (conversationId === currentChat?._id) {
        dispatchMessages({ type: 'RESET' });
      }
    };

    const handleTyping = (data) => {
      if (!typingSoundRef.current) {
        typingSoundRef.current = new Audio(TYPING_SOUND_URL);
      }
      setIsTypingDisplay(data.typing);
      setSenderPlayerId(data.senderPlayerId);
      if (data.typing) typingSoundRef.current.play().catch(() => {});
      else typingSoundRef.current.pause();
    };

    socket.on('getMessage', handleIncomingMessage);
    socket.on('messageReadByReceiver', handleMessageRead);
    socket.on('getClearChat', handleClearChat);
    socket.on('display', handleTyping);

    return () => {
      socket.off('getMessage', handleIncomingMessage);
      socket.off('messageReadByReceiver', handleMessageRead);
      socket.off('getClearChat', handleClearChat);
      socket.off('display', handleTyping);
    };
  }, [
    socket,
    currentChat?._id,
    currentChat?.members,
    isHistoryLoaded,
    player.data.player._id,
    player.data.player.followings,
    refetchConversations,
  ]);

  useEffect(() => {
    const cleanup = registerSocketEvents();
    return () => cleanup && cleanup();
  }, [registerSocketEvents]);

  // Emit typing
  useEffect(() => {
    const receiverId = currentChat?.members?.find(
      (m) => m !== player.data.player._id,
    );
    if (!receiverId) return;
    socket?.emit('typing', {
      senderId: player.data.player._id,
      senderPlayerId: player.data.player.playerId,
      receiverId,
      typing: isTyping,
    });
  }, [
    isTyping,
    currentChat?.members,
    socket,
    player.data.player._id,
    player.data.player.playerId,
  ]);

  // Upload helper
  const uploadAsset = useCallback(
    async (rawFile, explicitType) => {
      if (!rawFile) return '';

      setProgress(0);
      const { file: preparedFile } = await prepareMessengerUploadFile(rawFile, {
        maxImageBytes: MAX_IMAGE_SIZE,
        maxVideoBytes: MAX_VIDEO_SIZE,
      });

      const result = await uploader.upload(preparedFile, {
        publicIdPrefix:
          explicitType === 'audio' ? 'Messenger/Audio' : 'Messenger/Media',
        onProgress: (pct) => setProgress(pct),
        transform: true,
      });

      const url = result?.transformedUrl || result?.secureUrl || '';
      if (!url) throw new Error('Upload completed without a media URL.');
      setProgress(100);
      return url;
    },
    [uploader],
  );

  // Rate limit / suspension
  const checkSuspension = useCallback(
    async () =>
      handleSuspension(
        messageTimestamps,
        setMessageTimestamps,
        setIsSuspended,
        MESSAGE_LIMIT,
        TIME_WINDOW,
        SUSPENSION_TIME,
        player.data.player._id,
      ),
    [messageTimestamps, setIsSuspended, player.data.player._id],
  );

  // Send audio memo
  const handleSendAudioMemo = useCallback(async () => {
    if (!currentChat) return;
    if (isSendingRef.current || loading || sendingFile) return;
    try {
      isSendingRef.current = true;
      setLoading(true);
      setSendingFile(true);
      const receiverId = currentChat.members.find(
        (m) => m !== player.data.player._id,
      );
      const audioBlob = await fetch(mediaBlobUrl).then((r) => r.blob());
      const audioFile = new File([audioBlob], Date.now() + '.mp3', {
        type: 'audio/mpeg-3',
      });
      const assetUrl = await uploadAsset(audioFile, 'audio');
      const payload = {
        sender: player.data.player._id,
        text,
        img: assetUrl,
        conversationId: currentChat._id,
        isRead: false,
      };
      const res = await axiosConn.post('messages/false', payload, {
        headers: { Authorization: `Bearer ${player.token}` },
      });
      dispatchMessages({ type: 'APPEND', payload: res.data.data.data });
      socket?.emit('sendMessage', {
        senderId: player.data.player._id,
        senderPlayerId: player.data.player.playerId,
        receiverId,
        text,
        img: assetUrl,
        messageId: res.data.data.data._id,
        isRead: false,
      });
      setVoiceMemo(false);
      setText('');
      clearBlobUrl();
      showConfettiText();
    } catch (e) {
      setUploadError(getMessengerUploadErrorMessage(e));
    } finally {
      setLoading(false);
      setSendingFile(false);
      isSendingRef.current = false;
    }
  }, [
    currentChat,
    mediaBlobUrl,
    player.data.player._id,
    player.data.player.playerId,
    player.token,
    socket,
    text,
    uploadAsset,
    clearBlobUrl,
    showConfettiText,
    loading,
    sendingFile,
  ]);

  // Submit message
  const handleSubmit = useCallback(
    async (e) => {
      const evt = e || {};
      const isEnter = evt.key === 'Enter';
      const isClick = evt.type === 'click';
      const isSubmit = evt.type === 'submit';
      const hasContent = text.trim() !== '' || file;

      if (isEnter && (evt.shiftKey || evt.isComposing || evt.nativeEvent?.isComposing)) {
        return;
      }

      // Debug tap-in
      console.log('[Messenger][send] start', {
        isEnter,
        isClick,
        isSubmit,
        hasContent,
        textLen: text.length,
        hasFile: !!file,
        isSending: isSendingRef.current,
        loading,
        sendingFile,
        convo: currentChat?._id,
      });

      // Only react to Enter, form submit, or explicit click with content.
      if (!(isEnter || isClick || isSubmit) || !hasContent) return;

      if (isEnter) setText('');
      if (!currentChat) return;

      // Ignore if already sending
      if (isSendingRef.current || loading || sendingFile) return;

      const suspendedNow = await checkSuspension();
      console.log('[Messenger][send] suspension check', { suspendedNow });
      if (suspendedNow || isSuspended) return;

      let assetUrl = '';
      try {
        isSendingRef.current = true;
        setLoading(true);

        if (file) {
          setSendingFile(true);
          assetUrl = await uploadAsset(file);
        }

        const receiverId = currentChat.members.find(
          (m) => m !== player.data.player._id,
        );
        const msgPayload = {
          sender: player.data.player._id,
          text,
          img: assetUrl,
          conversationId: currentChat._id,
          isRead: false,
        };

        const res = await axiosConn.post('messages/false', msgPayload, {
          headers: { Authorization: `Bearer ${player.token}` },
        });

        dispatchMessages({ type: 'APPEND', payload: res.data.data.data });
        socket?.emit('sendMessage', {
          senderId: player.data.player._id,
          senderPlayerId: player.data.player.playerId,
          receiverId,
          text,
          img: assetUrl,
          messageId: res.data.data.data._id,
          isRead: false,
        });

        setText('');
        setFile(null);
        if (fileInput.current) fileInput.current.value = null;
        setSendingFile(false);

        const now = Date.now();
        if (now - startTime < TIME_WINDOW) {
          if (localMessageCount < MESSAGE_LIMIT) {
            setLocalMessageCount((c) => c + 1);
            showConfettiText();
          }
        } else {
          setLocalMessageCount(1);
          setStartTime(now);
          showConfettiText();
        }
      } catch (err) {
        setUploadError(getMessengerUploadErrorMessage(err));
        setSendingFile(false);
      } finally {
        setLoading(false);
        isSendingRef.current = false;
      }
    },
    [
      checkSuspension,
      currentChat,
      file,
      isSuspended,
      localMessageCount,
      player.data.player._id,
      player.data.player.playerId,
      player.token,
      showConfettiText,
      socket,
      startTime,
      text,
      uploadAsset,
      loading,
      sendingFile,
    ],
  );

  // Mic toggle for voice memo
  const handleMicClick = useCallback(() => {
    setVoiceMemo(true);
    if (!isRecording) startRecording();
    else stopRecording();
    setIsRecording((p) => !p);
  }, [isRecording, startRecording, stopRecording]);

  // Delete message
  const handleDeleteMessage = useCallback(
    (messageId) => dispatchMessages({ type: 'DELETE', id: messageId }),
    [],
  );

  // Visible messages
  const visibleMessages = useMemo(() => {
    const startIdx = Math.max(0, messages.length - messageCount);
    return messages.slice(startIdx);
  }, [messages, messageCount]);

  // Online helper
  const getPresenceTimestamp = useCallback((entry) => {
    if (!entry) return 0;
    const ts =
      entry.lastSeenAt ??
      entry.lastSeen ??
      entry.lastUpdatedAt ??
      entry.updatedAt ??
      entry.timestamp ??
      entry.wentOfflineAt ??
      0;
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    const parsed = new Date(ts).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const requestPresenceSnapshot = useCallback(() => {
    if (!socket) return;
    const now = Date.now();
    if (now - presenceSnapshotThrottleRef.current < 1000) return;
    presenceSnapshotThrottleRef.current = now;
    socket.emit('presence:snapshot:request');
  }, [socket]);

  const registerParticipant = useCallback((memberId, participant) => {
    const nextKeys = new Set(participantKeysRef.current);
    const candidates = [
      memberId,
      participant?._id,
      participant?.mongoId,
      participant?.accountId,
      participant?.playerId,
      participant?.handle,
    ]
      .filter(Boolean)
      .map((val) => String(val));
    let added = false;
    candidates.forEach((key) => {
      if (!nextKeys.has(key)) {
        nextKeys.add(key);
        added = true;
      }
    });
    participantKeysRef.current = nextKeys;
    if (added) requestPresenceSnapshot();
  }, [requestPresenceSnapshot]);

  const mergedPresenceMap = useMemo(() => {
    const keys = new Set([
      ...Array.from(presenceMap.keys()),
      ...Array.from(fallbackPresenceMap.keys()),
    ]);
    const next = new Map();
    keys.forEach((key) => {
      const primary = presenceMap.get(key);
      const fallback = fallbackPresenceMap.get(key);
      if (!primary && fallback) {
        next.set(key, fallback);
        return;
      }
      if (primary && !fallback) {
        next.set(key, primary);
        return;
      }
      if (primary && fallback) {
        const primaryTs = getPresenceTimestamp(primary);
        const fallbackTs = getPresenceTimestamp(fallback);
        next.set(key, fallbackTs > primaryTs ? fallback : primary);
      }
    });
    return next;
  }, [fallbackPresenceMap, presenceMap, getPresenceTimestamp]);

  const isMemberOnline = useCallback(
    (memberId) => {
      if (!memberId) return false;
      const entry = mergedPresenceMap.get(memberId);
      if (entry?.status) return entry.status !== 'offline';
      if (entry?.online) return true;
      for (const [, val] of mergedPresenceMap) {
        if (
          val?.source &&
          (val.source._id === memberId || val.source.playerId === memberId)
        )
          return !!val.online;
      }
      return false;
    },
    [mergedPresenceMap],
  );

  // Show more messages
  const showMoreMessages = () => {
    const el = messageListRef.current;
    let prevScrollHeight = 0;
    let prevTop = 0;
    if (el) {
      prevScrollHeight = el.scrollHeight;
      prevTop = el.scrollTop;
    }
    setMessageCount((p) => Math.min(p + MESSAGE_INCREMENT, messages.length));
    requestAnimationFrame(() => {
      if (el) {
        const newScrollHeight = el.scrollHeight;
        const diff = newScrollHeight - prevScrollHeight;
        el.scrollTop = prevTop + diff;
      }
    });
  };

  // Conversations
  const sortedConversations = useMemo(() => {
    const resolvePresence = (convo) => {
      const otherId = convo?.members?.find((m) => m !== player.data.player._id);
      if (!otherId) return { status: 'offline', lastSeen: null };
      const entry = mergedPresenceMap.get(otherId) || {};
      const lastSeen =
        entry.lastSeenAt ??
        entry.lastSeen ??
        entry.lastUpdatedAt ??
        entry.timestamp ??
        entry.updatedAt ??
        entry.wentOfflineAt ??
        null;
      let status = entry.status || entry.presenceStatus;
      if (status) {
        const normalized = String(status).toLowerCase();
        if (normalized === 'active' || normalized === 'available') status = 'online';
        else if (normalized === 'background') status = 'idle';
        else status = normalized;
      }
      if (!status) {
        if (entry.online === true) status = 'online';
        else if (entry.online === false) status = 'offline';
        else status = 'offline';
      }
      return { status, lastSeen };
    };
    const statusRank = (status) => {
      switch (status) {
        case 'online':
          return 0;
        case 'idle':
          return 1;
        case 'away':
          return 2;
        default:
          return 3;
      }
    };
    const list = conversations?.slice() || [];
    return list.sort((a, b) => {
      const pa = resolvePresence(a);
      const pb = resolvePresence(b);
      const rankA = statusRank(pa.status);
      const rankB = statusRank(pb.status);
      if (rankA !== rankB) return rankA - rankB;
      if (pa.lastSeen && pb.lastSeen && pa.lastSeen !== pb.lastSeen) {
        return pb.lastSeen - pa.lastSeen;
      }
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    });
  }, [conversations, mergedPresenceMap, player.data.player._id]);
  const visibleConversations = useMemo(
    () => sortedConversations.slice(0, conversationCount),
    [sortedConversations, conversationCount],
  );
  const hiddenConvoCount = Math.max(
    0,
    sortedConversations.length - conversationCount,
  );
  const expandConversations = () =>
    setConversationCount((c) => {
      setHasExpanded(true);
      return Math.min(c + CONVO_INCREMENT, sortedConversations.length);
    });
  const collapseConversations = () =>
    setConversationCount(() => {
      setHasExpanded(false);
      return mobileView ? MOBILE_INITIAL_CONVO_LIMIT : INITIAL_CONVO_LIMIT;
    });
  const [hasExpanded, setHasExpanded] = useState(false);

  // Call trigger ONLY
  const handleStartCall = useCallback(
    (isVideo) => {
      if (!currentChat || !player?.data?.player?._id) return;

      const me = player.data.player._id;
      const otherId = currentChat.members?.find((m) => m !== me);
      if (!otherId || !currentChat._id) return;

      startCallFromContext({
        peerId: otherId,
        convoId: currentChat._id,
        isVideo: !!isVideo,
      });
    },
    [currentChat, player, startCallFromContext],
  );

  return (
    <div
      className={`${!mobileView ? 'messenger' : 'messengerMobile'} ${
        darkTheme ? 'app dark-theme' : ''
      }`}
    >
      {/* LEFT RAIL */}
      <div className={!mobileView ? 'leftRail' : 'leftRailMobile'}>
        <div className="chatMenu" aria-label="Conversations list">
          <div className="chatMenuWrapper enhancedScroll">
            <div
              className="conversationList"
              role="list"
              id="conversationList"
              aria-label="Recent conversations"
            >
              {visibleConversations.map((c) => {
                const otherId = c.members.find(
                  (m) => m !== player.data.player._id,
                );
                const online = isMemberOnline(otherId);
                return (
                  <div role="listitem" key={c._id}>
                    <Conversation
                      presenceMap={mergedPresenceMap}
                      conversation={c}
                      currentPlayer={player}
                      setMessages={(list) =>
                        dispatchMessages({ type: 'SET_ALL', payload: list })
                      }
                      setCurrentChat={setCurrentChat}
                      currentChat={currentChat}
                      arrivalMessage={null}
                      isOnline={online}
                      onParticipantResolved={registerParticipant}
                    />
                  </div>
                );
              })}
              {hiddenConvoCount > 0 && (
                <div className="conversationListFade" aria-hidden="true" />
              )}
            </div>
            {sortedConversations.length > INITIAL_CONVO_LIMIT && (
              <div
                className={`conversationListControls ${
                  hasExpanded ? 'dual' : ''
                }`}
                aria-label="Conversation list controls"
              >
                {hasExpanded && (
                  <button
                    className="convoControlBtn"
                    onClick={collapseConversations}
                    aria-controls="conversationList"
                    aria-expanded="true"
                  >
                    Show less
                  </button>
                )}
                <button
                  className="convoControlBtn"
                  onClick={expandConversations}
                  aria-controls="conversationList"
                  aria-expanded={hiddenConvoCount === 0 ? 'true' : 'false'}
                  disabled={hiddenConvoCount === 0}
                >
                  Show {hiddenConvoCount} more
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT: Chat area */}
      <div className={!mobileView ? 'chatBox' : 'chatBoxMobile'}>
        <div className="chatBoxWrapper">
          <LoadingBar
            color="limegreen"
            progress={progress}
            onLoaderFinished={() => setProgress(0)}
          />
          {currentChat ? (
            <>
              <div className="callQuickActions">
                <button
                  className="callBtn"
                  style={{ minWidth: 120, height: 36 }}
                  onClick={() => handleStartCall(false)}
                >
                  Voice Call
                </button>

                <button
                  className="callBtn"
                  style={{ minWidth: 120, height: 36 }}
                  onClick={() => handleStartCall(true)}
                >
                  Video Call
                </button>
              </div>

              <div
                className="chatBoxTop enhancedScroll"
                ref={messageListRef}
                aria-live="polite"
              >
                {(messageCount < messages.length ||
                  messageCount > DEFAULT_MESSAGE_COUNT) && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                      marginBottom: '10px',
                      marginTop: '2px',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    {messageCount < messages.length && (
                      <div
                        className={
                          !mobileView ? 'viewMessages' : 'viewMessagesMobile'
                        }
                        onClick={showMoreMessages}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && showMoreMessages()
                        }
                        aria-label="Load more messages"
                        style={{ minWidth: 120 }}
                      >
                        View{' '}
                        {Math.min(
                          MESSAGE_INCREMENT,
                          messages.length - messageCount,
                        )}{' '}
                        more messages
                      </div>
                    )}
                    {messageCount > DEFAULT_MESSAGE_COUNT && (
                      <div
                        className={
                          !mobileView ? 'viewMessages' : 'viewMessagesMobile'
                        }
                        onClick={() => {
                          setMessageCount(DEFAULT_MESSAGE_COUNT);
                          requestAnimationFrame(() =>
                            bottomAnchorRef.current?.scrollIntoView({
                              behavior: 'smooth',
                            }),
                          );
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Collapse messages"
                        style={{
                          minWidth: 120,
                          background: 'linear-gradient(135deg,#7f7fd5,#86a8e7)',
                          color: '#fff',
                          fontWeight: 700,
                        }}
                      >
                        View less messages
                      </div>
                    )}
                  </div>
                )}

                {messageCount < messages.length && !nearTop && (
                  <button
                    type="button"
                    className="viewMessagesFloating show"
                    aria-label="Show older messages"
                    onClick={showMoreMessages}
                  >
                    + More
                  </button>
                )}
                {visibleMessages.map((m, idx) => (
                  <div
                    className="messages"
                    key={m._id || m.messageId || `${m.createdAt || 'msg'}-${idx}`} // ensure unique key per render
                  >
                    <Message
                      message={m}
                      own={m.sender === player.data.player._id}
                      senderProfilePhoto={player.data.player.profilePhoto}
                      receiverProfilePhoto={receiverProfilePhoto}
                      from="Messenger"
                      onDeleteMessage={handleDeleteMessage}
                      receiverId={currentChat.members.find(
                        (member) => member !== player.data.player._id,
                      )}
                    />
                  </div>
                ))}
                <div ref={bottomAnchorRef} />
              </div>

              {!atBottom && (
                <button
                  type="button"
                  className="viewMessagesFloating show"
                  style={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    background: 'linear-gradient(135deg,#8a2be2,#4f46e5)',
                    fontSize: 22,
                    padding: '8px 14px',
                    borderRadius: '50%',
                    width: 44,
                    height: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 6px 20px -6px rgba(0,0,0,0.35)',
                    zIndex: 1000,
                  }}
                  aria-label="Scroll to bottom"
                  onClick={() =>
                    bottomAnchorRef.current?.scrollIntoView({
                      behavior: 'smooth',
                    })
                  }
                >
                  ↓
                </button>
              )}

              <ConfettiTextComponent />

              {/* Typing indicator */}
              {isTypingDisplay && senderPlayerId && (
                <TypingIndicator senderPlayerId={senderPlayerId} />
              )}

              {/* Middle controls: voice memo, file input, etc. */}
              <ChatBoxMiddle
                isTypingDisplay={isTypingDisplay}
                senderPlayerId={senderPlayerId}
                status={recStatus}
                mediaBlobUrl={mediaBlobUrl}
                loading={loading}
                handleMicClick={handleMicClick}
                isRecording={isRecording}
                mobileView={mobileView}
                width={width}
                setVoiceMemo={setVoiceMemo}
                clearBlobUrl={clearBlobUrl}
                handleSendAudioMemo={handleSendAudioMemo}
                fileInput={fileInput}
                handleFileInput={(e) =>
                  handleFileInput(
                    e,
                    setUploadError,
                    setFile,
                    MAX_VIDEO_SIZE,
                    MAX_IMAGE_SIZE,
                  )
                }
                error={uploadError}
                setError={setUploadError}
                setFile={setFile}
                setText={setText}
                file={file}
                setLoading={setLoading}
                setIsLoading={setLoading}
                setSendingFile={setSendingFile}
                messageRef={textRef}
                handleSubmit={handleSubmit}
                accept={MESSENGER_UPLOAD_ACCEPT}
                maxFileMB={MAX_VIDEO_SIZE_MB}
              />

              {/* Bottom text input / emoji / send */}
              {!voiceMemo && (
                <ChatBoxBottom
                  voiceMemo={voiceMemo}
                  picker={picker}
                  menu={menuRef}
                  handleEmoji={(event, emojiObject) =>
                    insertEmoji(
                      emojiObject?.emoji,
                      textRef,
                      text,
                      setText,
                      setCursorPosition,
                    )
                  }
                  textRef={textRef}
                  register={register}
                  text={text}
                  setText={setText}
                  handleSubmit={handleSubmit}
                  loading={loading}
                  sendingFile={sendingFile}
                  handleMicClick={handleMicClick}
                  isRecording={isRecording}
                  setPicker={setPicker}
                  fileInput={fileInput}
                  setSendingFile={setSendingFile}
                  handleSendAudioMemo={handleSendAudioMemo}
                />
              )}
            </>
          ) : isLoadingMessages ? (
            <div className="openConversation">
              <CircularProgress color="inherit" size="22px" />
            </div>
          ) : (
            <div className="openConversation">
              <span
                className={
                  !mobileView
                    ? 'noConversationText flex-inline'
                    : 'noConversationTextMobile flex-inline'
                }
              >
                Open a conversation to start a chat.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Call UI removed — handled by global CallOverlay */}
    </div>
  );
}
